import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import EnhancedCheckbox from '../EnhancedCheckbox';
import FloatingActionButton from '../FloatingActionButton';

import { useScrollLoadingContext } from '../../Contexts/ScrollLoadingContext';
import { useAddCampaignContent, useCreateMediaMutation } from "../../../queries";
import { useAddPlaylistContent } from "../../../queries/playlistContent";
import { handleResErrors, useInvalidateCampaignLayer } from "../../../queries/utils";
import { setLastElement, galleryRefetch, setParams } from '../../../redux/Slices/scrollSlice';
import { setAssign, setDisableBtn, toggleDialog } from '../../../redux/Slices/Global/globalSlice';
import { generateString } from '../../../config/global';
import { uploadToGcs } from '../../../lib/gcsUpload';
import { useCustomToast } from '../../Contexts/ToastContext';
import axiosInstance from '../../../config/Axios';

import ScrollContainer from '../ScrollContainer';
import BreadCrumb from "../../Media/components/MediaLayout/BreadCrumb";
import FolderCard from "../../Media/components/MediaLayout/FolderCard";
import MediaCard from "../../Media/components/MediaLayout/MediaCard";
import LazyImage from "../ShowMedia/LazyImage";
import { ProgressSpinner } from 'primereact/progressspinner';
import UploadPopup from '../../Media/components/UploadPopup/UploadPopup';
import { EMPTY_FOLDER } from '../../../config/media';
import AddContentSearchInput from './AddContentSearchInput';
import FirebaseMediaListener from '../../Services/FirebaseMediaListener';
import { useMediaPreviewOptional, isVisual } from '../MediaPreview/MediaPreviewContext';
import MiniPlayer from '../MediaPreview/MiniPlayer';

// Long-press delay (ms) before a touch pointerdown on a file-tile opens its
// preview instead of toggling selection — ported from the prototype's own
// 450ms timer (pp-campaign-builder-prototype.html:926).
const LONG_PRESS_MS = 450;

/**
 * v15 media-preview file tile — corner `.pvbtn` (expand icon for image/
 * video, play/pause for audio, reflecting LIVE playing state), long-press
 * (touch only, via pointerdown/pointerType==='touch') opens preview WITHOUT
 * toggling selection, click toggles selection unless the click landed on
 * the `.pvbtn` itself. Ported from the prototype's `tile()` (~891-899) and
 * its long-press wiring (~924-929).
 */
function FileTile({ item, isLastElement, lastElementRef, selected, isFresh, onToggleSelect, onPreview, isAudio, isPlaying, t }) {
  const longPressTimer = useRef(null);
  const firedRef = useRef(false);
  const type = String(item.type || '').toUpperCase();

  const clearLp = () => { clearTimeout(longPressTimer.current); };

  const onPointerDown = (e) => {
    if (e.pointerType !== 'touch') return;
    firedRef.current = false;
    longPressTimer.current = setTimeout(() => {
      firedRef.current = true;
      if (navigator.vibrate) navigator.vibrate(10);
      onPreview(item);
    }, LONG_PRESS_MS);
  };

  const onClick = (e) => {
    if (firedRef.current) { firedRef.current = false; return; }
    if (e.target.closest('.pvbtn')) { e.stopPropagation(); onPreview(item); return; }
    onToggleSelect(item);
  };

  return (
    <button
      type="button"
      ref={isLastElement ? lastElementRef : null}
      className={`pp-cb-file-tile ${selected ? 'selected' : ''} ${isAudio && isPlaying ? 'playing' : ''}`}
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerUp={clearLp}
      onPointerLeave={clearLp}
      onPointerCancel={clearLp}
      onContextMenu={(e) => { if (firedRef.current) e.preventDefault(); }}
    >
      <span className={`th ${type === 'AUDIO' ? 'audio' : ''}`}>
        {type === 'AUDIO' ? (
          <i className="pi pi-volume-up" />
        ) : (
          <LazyImage media={item} popupType="media_preview" styleClass="pp-cb-file-thumb-img" mountPreview={false} />
        )}
        {type === 'VIDEO' && (
          <span className="playbadge"><i className="pi pi-play" /></span>
        )}
        <span
          className="pvbtn"
          role="button"
          tabIndex={0}
          aria-label={type === 'AUDIO' ? t('addcontent.listen_label') : t('addcontent.preview_label')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onPreview(item); } }}
        >
          <i className={type === 'AUDIO' ? (isPlaying ? 'pi pi-pause' : 'pi pi-play') : 'pi pi-eye'} />
        </span>
        <span className="selmark"><i className="pi pi-check" /></span>
        {isFresh && <span className="newtag">{t('addcontent.new_tag')}</span>}
      </span>
      <span className="cap">{(item.file_name || item.name || '').replace(/_/g, ' ')}</span>
    </button>
  );
}

/**
 * Add Content → Media tab.
 *
 * Folder-aware picker — mirrors the Media Gallery's navigation model.
 * Folders render as clickable rows (FolderCard) that drill into the
 * folder via redux `params.folder_id`; BreadCrumb walks back out.
 *
 * Why per_page=999: this is a picker, not a gallery. The default
 * 35-row page combined with the IntersectionObserver inside the
 * dialog often fails to load page 2 — the modal viewport is short,
 * the sentinel doesn't re-intersect, and the user sees only a slice
 * of the folder. One large page sidesteps the broken infinite scroll
 * entirely and shows the whole folder at once.
 */
function MediaList({ builderRow = false, vibesMode = false }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams();
  const lastElementRef = useRef(null);
  const fileInputRef = useRef(null);

  const { assign, layer, campaignDetails, companyDetails } = useSelector((state) => state.global);
  const { isFetching, data, params } = useSelector((state) => state.scroll);
  const { ids, setIds } = useScrollLoadingContext();
  const screenDetails = useSelector((state) => state.layout.screenDetails);
  // v15 media preview (builderRow only) — see openPreview's own doc comment
  // below for why this needs to be a ref; declared unconditionally here
  // (not inside the builderRow branch) per rules-of-hooks.
  const idsRef = useRef(ids);
  idsRef.current = ids;

  const addCampaignContent = useAddCampaignContent();
  const addPlaylistContent = useAddPlaylistContent();
  const queryClient = useQueryClient();
  const { showToast } = useCustomToast();
  // Same invalidation MAIN's useAddCampaignContent() success handler already
  // runs (see useInvalidateCampaignLayer in queries/utils.js) — specifically
  // ['getCampaign', id], the query that carries has_unpublished_changes. The
  // raw-axios vibes orchestration below bypasses that hook entirely (see its
  // own doc comment for why), so without this call the "needs publish" notice
  // only appeared after a manual reload — confirmed live on dev 2026-10-01.
  const invalidateCampaignLayer = useInvalidateCampaignLayer();

  // v15 media preview — null outside a <MediaPreviewProvider> (see that
  // hook's own doc comment); only InteractiveCampaignSetup.jsx/ScreenInfo.jsx
  // render this component with builderRow=false, so every code path below
  // that touches `mediaPreview` is itself gated on `builderRow`.
  const mediaPreview = useMediaPreviewOptional();

  // Sheet-close stop condition (prototype's closeSheet() -> auStop(),
  // ~pp-campaign-builder-prototype.html:977): AddContentPopup/MediaList only
  // ever mounts while the sheet is open (CampaignContentSetup.jsx/
  // PlaylistSetup.jsx both conditionally render <AddContentPopup/> on
  // openDialog.addContent), so unmount IS "the sheet closed".
  useEffect(() => {
    return () => { if (builderRow) mediaPreview?.audio?.stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [currentFolder, setCurrentFolder] = useState(EMPTY_FOLDER);
  const [breadCrumb, setBreadCrumb] = useState([]);

  // ── Builder-sheet folder navigation (Item 2 rebuild) — same redux
  // params.folder_id + localStorage mirroring FolderCard.getSubFolderMedia()
  // / BreadCrumb.goHome() already use for the real Media Gallery, just
  // invoked directly instead of through those two components (the sheet
  // no longer renders FolderCard/BreadCrumb's own DOM — see the builderRow
  // branch below).
  const enterFolder = (item) => {
    if (isFetching) return;
    const name = item.folder_name || item.name || '';
    const nextCrumb = [...breadCrumb, { label: name, id: item.id }];
    setCurrentFolder({ id: item.id, name });
    setBreadCrumb(nextCrumb);
    dispatch(setParams({ ...params, folder_id: item.id }));
    localStorage.setItem('bread_crumb', JSON.stringify(nextCrumb));
    localStorage.setItem('folder_id', item.id);
  };
  const backToRoot = () => {
    setCurrentFolder(EMPTY_FOLDER);
    setBreadCrumb([]);
    dispatch(setParams({ ...params, folder_id: '' }));
    localStorage.setItem('bread_crumb', JSON.stringify([]));
    localStorage.setItem('folder_id', '');
  };

  // ── Upload-from-picker (builder sheets only, both MAIN and vibes) ──────
  // Reuses the EXACT uploadToGcs + useCreateMediaMutation chain the real
  // Media Gallery's UploadMedia.jsx uses — no new upload implementation.
  const [uploadRows, setUploadRows] = useState({}); // { [fileName]: percent | 'failed' }
  const [uploadCounter, setUploadCounter] = useState(0);
  const [freshIds, setFreshIds] = useState([]); // media ids uploaded THIS sheet session — "جديد" badge + sort-first
  const createMediaMutation = useCreateMediaMutation(setUploadRows, setUploadCounter);

  const handleFilesSelected = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploadCounter((c) => c + files.length);

    for (const file of files) {
      setUploadRows((prev) => ({ ...prev, [file.name]: 0 }));
      try {
        let fileType = file.type === "" ? file.name.slice(file.name.lastIndexOf('.') + 1)
          : file.type.includes("quicktime") ? file.name?.split(".")[1]
          : file.type?.split("/")[1];
        fileType = fileType?.replace(/json\+|avi\./g, (m) => (m === "json+" ? "" : "avi"));

        const hashedFile = generateString(30, fileType || '');
        if (!hashedFile) {
          setUploadRows((prev) => ({ ...prev, [file.name]: 'failed' }));
          setUploadCounter((c) => c - 1);
          continue;
        }

        const companyId = companyDetails?.company?.id || localStorage.getItem("company_id");
        const objectKey = `${companyId}/${currentFolder?.id || "root"}/${hashedFile}`;

        const publicUrl = await uploadToGcs(file, objectKey, (pct) => {
          setUploadRows((prev) => ({ ...prev, [file.name]: Math.round(pct / 2) }));
        });

        const created = await createMediaMutation.mutateAsync({
          file,
          fileUrl: publicUrl,
          folderId: currentFolder?.id || null,
        });

        const newId = created?.data?.media_file;
        if (newId) setFreshIds((prev) => [...prev, `${newId}`]);
      } catch (err) {
        console.error(`Upload failed for ${file.name}:`, err);
        setUploadRows((prev) => ({ ...prev, [file.name]: 'failed' }));
        setUploadCounter((c) => c - 1);
      }
    }

    // Refresh the folder listing so the new item(s) actually appear —
    // useCreateMediaMutation already pushed the new id(s) into `ids`
    // (picker selection) when the add-content dialog is open, see
    // folderMedia.js's useCreateMediaMutation onSuccess.
    dispatch(galleryRefetch());
  };

  useEffect(() => {
    if (lastElementRef.current) {
      dispatch(setLastElement(lastElementRef.current));
    }
  }, [lastElementRef.current]);

  const param = {
    is_interactive: Number(campaignDetails?.is_interactive ?? 0),
    section: "MEDIA",
    layer,
    per_page: 999,
  };

  useEffect(() => {
    return () => setIds([]);
  }, []);

  useEffect(() => {
    if (assign) {
      dispatch(setDisableBtn(true));
      assignHandler();
    }
  }, [assign]);

  // ── B2 orchestration — a vibes ("الصور (أجواء)") add on PLAYGROUND is NOT
  // a plain campaign/content/assign(type:media) call: it must land inside
  // this campaign's OWN "Auto-created"-tagged playlist (creating one on
  // first use), exactly mirroring Fast Create's own playlist mechanism.
  // Raw axios calls throughout (not the existing hooks) — several of those
  // hooks (useCreatePlaylistMutation navigates to /playlist/:id/show,
  // useAddPlaylistContent/useAddCampaignContent close the addContent dialog
  // on success) carry side effects meant for their OWN callers; firing them
  // mid-sequence here would navigate away from the campaign builder or close
  // the sheet before every step finished. The dialog is closed / toast shown
  // / queries invalidated exactly once, after the whole sequence succeeds.
  const AUTO_TAG_NAME = "Auto-created";

  // ── F7 safety net — the player's image-display timer does
  // `media.customDuration!!` (Kotlin non-null assertion; see
  // PlayerFeature.kt:37 / ExoPlayerNewFeature.kt:316 / AudioPlayerFeature.kt:220
  // / ExoArchive.kt:250), a live crash risk on null/0. `assignPlaylistMedia()`
  // (POST /playlist/media/assign, what this orchestration calls) sets
  // `duration => $media->duration` verbatim with NO `=== 0 ? fallback` —
  // unlike CafeWizardService::mediaDuration()/attachMedia(), which explicitly
  // fall back to 8s for a zero/null-duration IMAGE. A freshly-uploaded image
  // gets duration=10 from MediaController's own upload defaults (confirmed:
  // every image extension maps to 'duration' => 10), so this should not fire
  // in the common case — this is a backstop for the cases it doesn't cover
  // (older/migrated media rows whose duration is 0 or null), fixed through
  // the EXISTING playlist-item settings endpoint, no new backend code.
  const ensureSafeImageDurations = async (playlistId, addedMediaIds) => {
    try {
      const { data: mediaResp } = await axiosInstance.get(`/playlist/${playlistId}/PLAYGROUND/media`);
      const rows = mediaResp?.data || [];
      const addedSet = new Set(addedMediaIds.map((v) => `${v}`));
      const unsafe = rows.filter((row) =>
        addedSet.has(`${row.media_id}`) &&
        String(row.type || '').toUpperCase() === 'IMAGE' &&
        !(Number(row.custom_duration) > 0)
      );
      for (const row of unsafe) {
        await axiosInstance.post('/playlist/media/setting', {
          playlist_media_id: row.playlist_media_id,
          duration: 10,
        });
      }
    } catch (e) {
      // Non-fatal: the add itself already succeeded. Logged, not thrown —
      // worst case an image keeps whatever duration it already had.
      console.error('Vibes add: image duration safety-net check failed', e);
    }
  };

  const findExistingAutoPlaylistId = async (campaignId) => {
    const { data: rowsResp } = await axiosInstance.get(`/campaign/content/${campaignId}/PLAYGROUND`);
    const rows = rowsResp?.data || [];
    for (const row of rows) {
      if (!row.playlist_id) continue;
      try {
        const [{ data: plResp }, { data: impactResp }] = await Promise.all([
          axiosInstance.get(`/playlist/${row.playlist_id}`),
          axiosInstance.get(`/playlist/${row.playlist_id}/publish-impact`),
        ]);
        const tagsList = plResp?.data?.tags_list || [];
        const hasAutoTag = tagsList.some((tg) => tg.name === AUTO_TAG_NAME);
        const campaignCount = impactResp?.data?.campaign_count ?? 0;
        if (hasAutoTag && campaignCount <= 1) return row.playlist_id;
      } catch (e) {
        // one row's lookup failing shouldn't abort the scan — keep looking
        console.error('Vibes add: failed inspecting playlist row', row.playlist_id, e);
      }
    }
    return null;
  };

  const runVibesAdd = async () => {
    const campaignId = id;
    const campaignName = campaignDetails?.name || '';

    try {
      const existingId = await findExistingAutoPlaylistId(campaignId);

      if (existingId) {
        await axiosInstance.post('/playlist/media/assign', {
          playlist_id: existingId,
          list_media_ids: ids,
          layer: 'PLAYGROUND',
        });
        await ensureSafeImageDurations(existingId, ids);
      } else {
        const { data: createResp } = await axiosInstance.post('/playlist/create', {
          name: `أجواء · ${campaignName}`,
        });
        const newPlaylistId = createResp?.data?.id;
        if (!newPlaylistId) throw new Error('Playlist create returned no id');

        try {
          await axiosInstance.put(`/tag/${newPlaylistId}/update`, {
            tagged_id: newPlaylistId,
            tags: [{ id: null, name: AUTO_TAG_NAME, isNew: true, deleted: false }],
          });

          await axiosInstance.post('/playlist/media/assign', {
            playlist_id: newPlaylistId,
            list_media_ids: ids,
            layer: 'PLAYGROUND',
          });
          await ensureSafeImageDurations(newPlaylistId, ids);

          await axiosInstance.post('/campaign/content/assign', {
            ids_list: [newPlaylistId],
            campaign_id: campaignId,
            layer: 'PLAYGROUND',
            type: 'playlist',
          });

          const { data: rowsResp2 } = await axiosInstance.get(`/campaign/content/${campaignId}/PLAYGROUND`);
          const newRow = (rowsResp2?.data || []).find((r) => r.playlist_id === newPlaylistId);
          if (!newRow) throw new Error('Playlist attached but its campaign row could not be located');

          const settingForm = new FormData();
          settingForm.append('is_muted', 1);
          settingForm.append('maintain_aspect_ratio', 1);
          settingForm.append('downloadable', 0);
          settingForm.append('show_layer_one', 0);
          settingForm.append('show_layer_two', 0);
          settingForm.append('show_layer_three', 0);
          settingForm.append('show_video_screen', Number(newRow.show_video_screen ?? 0));
          settingForm.append('is_showed', Number(newRow.is_showed ?? 0));
          await axiosInstance.post(`/campaign/media/${newRow.campaign_playlist_media_id}/setting`, settingForm);
        } catch (innerErr) {
          // Per spec: a failure after the playlist was created is left as-is
          // (no auto-rollback/retry) — diagnosable via this console line.
          console.error('Vibes add: playlist', newPlaylistId, 'was created but a later step failed', innerErr);
          throw innerErr;
        }
      }

      dispatch(setDisableBtn(false));
      dispatch(setAssign(false));
      dispatch(toggleDialog('addContent'));
      setIds([]);
      queryClient.invalidateQueries(['getCampaignLayerContent', campaignId, 'PLAYGROUND']);
      queryClient.invalidateQueries(['getPlaylist']);
      queryClient.invalidateQueries(['getPlaylistLayerContent']);
      queryClient.invalidateQueries(['playlistPublishImpact']);
      queryClient.invalidateQueries({ queryKey: ["companyPlaylists"] });
      // ['getCampaign', campaignId] — the campaign-details query that carries
      // has_unpublished_changes — was missing from the list above; MAIN's own
      // add path gets it for free via useAddCampaignContent()'s onSuccess.
      invalidateCampaignLayer({ campaign_id: campaignId });
      showToast('success', t('addcontent.title'), t('addcontent.added_success'));
    } catch (err) {
      dispatch(setDisableBtn(false));
      dispatch(setAssign(false));
      console.error('Vibes add-content orchestration failed', err);
      handleResErrors(err, showToast);
    }
  };

  const assignHandler = async () => {
    // Prototype's addBtn.onclick calls auStop() before pushing selections
    // (~pp-campaign-builder-prototype.html:888) — a no-op when nothing is
    // playing, so safe to call unconditionally here.
    if (builderRow) mediaPreview?.audio?.stop();
    if (window.location.pathname.includes("campaign")) {
      if (builderRow && layer === 'PLAYGROUND') {
        await runVibesAdd();
        return;
      }
      await addCampaignContent.mutateAsync({
        "ids_list": ids,
        "campaign_id": id,
        "layer": layer,
        "type": "media",
      });
    } else if (window.location.pathname.includes("layout")) {
      await addCampaignContent.mutateAsync({
        "ids_list": ids,
        "campaign_id": screenDetails.id,
        "layer": layer,
        "type": "media",
      });
    } else {
      await addPlaylistContent.mutateAsync({
        "list_media_ids": ids,
        "playlist_id": id,
        "layer": layer,
      });
    }
  };

  // Functional update, keyed by STRINGIFIED id — immune to stale closures.
  // Previously this read `ids` from the enclosing render (`let selectedMedia
  // = [...ids]`) and pushed the raw (non-stringified) `e.value`. That was
  // fine for the grid (each click re-renders this component, so the closure
  // was never stale for more than one tick) but broke the lightbox: its
  // `toggle` callback is handed to the MediaPreviewContext ONCE when the
  // lightbox opens and never refreshed, so the ONE `onMediaChange` instance
  // captured there stayed pinned to the `ids` snapshot from opening time —
  // every subsequent toggle rebuilt from that same stale base, silently
  // discarding earlier selections made inside the lightbox (confirmed via
  // direct code reading, MediaList.jsx/Lightbox.jsx). Using the functional
  // `setIds(prev => ...)` form means every caller — however stale its own
  // closure is — always toggles against the CURRENT state. Also switched to
  // pure toggle-by-id-membership instead of trusting the caller's `e.checked`
  // direction: every call site here already computes `checked` as "not
  // currently selected" (`!selected`, or an `indexOf(...) === -1` check), so
  // this is behaviorally identical while removing a second, now-redundant
  // source of staleness. Stringifying with `${...}` matches the convention
  // already used everywhere else `ids` is read in this file (e.g. line 639's
  // `ids.indexOf(\`${item.id}\`)`), ruling out an id (string) vs id (number)
  // identity mismatch as a compounding cause — `ids` elsewhere is always
  // compared/stored as strings; the old code's raw `e.value` push was the
  // only place that could have broken that invariant.
  const onMediaChange = (e) => {
    const idStr = `${e.value}`;
    setIds((prev) => (
      prev.includes(idStr) ? prev.filter((x) => x !== idStr) : [...prev, idStr]
    ));
  };

  // ── Campaign builder / playlist-page add-content sheet — STRUCTURAL
  // rebuild (not a reskin) of the prototype's own mediaView()/tile():
  // folders (root only) and files are two distinct sections, never mixed
  // into one grid; a file tile is a SQUARE real thumbnail (image/video via
  // LazyImage with mountPreview=false — same underlying thumb-loading
  // logic MediaGallery.jsx's own grid-tile already uses, not MediaCard's
  // wrapper chrome/menu/hover-overlay) with a play-badge (video) or music
  // icon (audio) and a selection checkmark overlay, captioned with the
  // file name ONLY — no type·size·duration line. Selection still routes
  // into this component's own `ids`/onMediaChange, unchanged.
  if (builderRow) {
    // vibesMode: audio is hidden entirely (music belongs in المحتوى الرئيسي,
    // not الصور (أجواء) / a non-MAIN playlist layer). Folders themselves are
    // NOT filtered/hidden here — the backend only returns a folder's TOTAL
    // media_count, with no per-type breakdown, and computing a real
    // filtered count would mean an extra fetch per visible folder (an N+1
    // this build does not add, no backend endpoint exists for it). A folder
    // containing only audio will still show its raw count and simply
    // render empty once opened — a known, disclosed gap, not a silent one.
    const rawList = data || [];
    const visibleList = vibesMode
      ? rawList.filter((item) => item.is_folder || String(item.type || '').toUpperCase() !== 'AUDIO')
      : rawList;

    const atRoot = breadCrumb.length === 0;
    // The prototype's mediaView() only ever models ONE folder level: a
    // folder-row at root, and entering a folder shows only that folder's
    // FILES — no nested folder-row at all. Matching that exactly (rather
    // than inventing recursive folder browsing the prototype never showed)
    // means a sub-folder nested inside a folder opened from this picker is
    // not reachable here — same kind of known, disclosed scope limit as
    // the vibesMode folder-count gap above, not a silent regression (the
    // full Media Gallery page still supports unlimited nesting).
    const folders = atRoot ? visibleList.filter((item) => item.is_folder) : [];
    const filesRaw = visibleList.filter((item) => !item.is_folder);
    // Freshly-uploaded items (this sheet session) sort first.
    const files = [...filesRaw].sort((a, b) => {
      const aFresh = freshIds.includes(`${a.id}`) ? 1 : 0;
      const bFresh = freshIds.includes(`${b.id}`) ? 1 : 0;
      return bFresh - aFresh;
    });

    const folderDisplayName = currentFolder?.name || t('addcontent.media_root_label');
    const uploadEntries = Object.entries(uploadRows);

    // v15 media preview — the sheet's own lightbox nav list excludes audio
    // (prototype: `var visual=cur.filter(function(o){return o.t!=='audio';})`,
    // ~pp-campaign-builder-prototype.html:921); audio tiles open the audio
    // engine directly via their own .pvbtn instead, never a lightbox entry.
    //
    // `isSel`/`toggle` are handed to the provider ONCE, at the moment the
    // lightbox opens, and the provider's own lightbox state only changes
    // identity on navigate/close — it does NOT re-render just because this
    // component (MediaList) re-renders with a new `ids` array. Closing over
    // `ids` directly here would freeze the lightbox's own "selected" button
    // at whatever `ids` was at open time, even though the real selection set
    // (and the grid behind it) keeps updating correctly. `idsRef` fixes
    // that: both closures read the LIVE value, and Lightbox.jsx forces its
    // own re-render right after calling `toggle` (mirroring the prototype's
    // own `LB.opts.toggle(o);lbRender();` pairing, ~line 859).
    const visualFiles = files.filter(isVisual);
    const openPreview = (item) => {
      if (!mediaPreview) return;
      const type = String(item.type || '').toUpperCase();
      if (type === 'AUDIO') { mediaPreview.audio.toggle(item); return; }
      const idx = visualFiles.indexOf(item);
      mediaPreview.openLightbox(visualFiles, idx < 0 ? 0 : idx, {
        canSelect: true,
        isSel: (o) => idsRef.current.indexOf(`${o.id}`) !== -1,
        toggle: (o) => onMediaChange({ checked: idsRef.current.indexOf(`${o.id}`) === -1, value: o.id }),
        // Deliberately NOT stopAudioOnClose — this is the sheet-grid preview
        // context, where the mini-player should keep playing after the
        // lightbox closes back to browsing (task spec, distinct from row
        // previews which DO stop on close).
      });
    };

    return (
      <div className="d-flex flex-column" style={{ minHeight: '40vh' }}>
        <div className="w-full mb-3">
          <AddContentSearchInput isDisabled={data?.length === 0} />
        </div>

        {!atRoot && (
          <div className="mb-3">
            <button type="button" className="pp-cb-crumb-row" onClick={backToRoot}>
              <i className="bx bx-chevron-right chev" />
              <span>{t('addcontent.media_root_label')} / {currentFolder?.name}</span>
            </button>
          </div>
        )}

        <button type="button" className="pp-cb-upload-btn" onClick={() => fileInputRef.current?.click()}>
          <span className="ui"><i className="bx bx-upload" /></span>
          <span className="ut">
            <span className="a">{t('addcontent.upload_from_device')}</span>
            <span className="b">{t('addcontent.saved_in_folder', { name: folderDisplayName })}</span>
          </span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,audio/*"
          hidden
          onChange={(e) => {
            handleFilesSelected(e.target.files);
            e.target.value = '';
          }}
        />
        {uploadEntries.length > 0 && (
          <div className="mb-2">
            {uploadEntries.map(([name, pct]) => (
              <div className="pp-cb-up-row" key={name}>
                <span className="un">{name}</span>
                <span className="bar"><span className="fill" style={{ width: `${pct === 'failed' ? 0 : pct}%` }} /></span>
                <span className="pc">{pct === 'failed' ? t('media.media_toast_upload_failed') : `${pct}%`}</span>
              </div>
            ))}
          </div>
        )}
        {vibesMode && (
          <div className="pp-cb-filter-note">
            <i className="pi pi-info-circle" />
            <span>{t('addcontent.vibes_filter_note')}</span>
          </div>
        )}

        <ScrollContainer path={`/media`} optionalParam={param}>
          {!isFetching && folders.length === 0 && files.length === 0 ? (
            <h5 className="py-5 text-center w-100">{t('addcontent.no_media_yet')}</h5>
          ) : (
            <>
              {atRoot && folders.length > 0 && (
                <>
                  <div className="pp-cb-sub-lbl">{t('addcontent.media_folders_label')}</div>
                  <div className="pp-cb-folder-row">
                    {folders.map((item) => (
                      <button
                        type="button"
                        key={item.id}
                        className="pp-cb-folder-tile"
                        onClick={() => enterFolder(item)}
                      >
                        <i className="bx bx-folder" />
                        <span className="fn">{item.folder_name || item.name}</span>
                        <span className="fc">{t('addcontent.folder_file_count', { count: item.media_count ?? 0 })}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              <div className="pp-cb-sub-lbl">{t('addcontent.media_files_label')}</div>
              {files.length === 0 ? (
                <div className="pp-cb-p-empty">{t('addcontent.media_files_empty')}</div>
              ) : (
                <>
                  {mediaPreview && (
                    <div className="pp-cb-pv-hint">
                      <i className="pi pi-info-circle" />
                      <span>{t(vibesMode ? 'addcontent.preview_hint_other' : 'addcontent.preview_hint_main')}</span>
                    </div>
                  )}
                  <div className="pp-cb-file-grid">
                    {files.map((item, index) => {
                      const isLastElement = index === files.length - 1;
                      const selected = ids.indexOf(`${item.id}`) !== -1;
                      const isFresh = freshIds.includes(`${item.id}`);
                      const type = String(item.type || '').toUpperCase();
                      return (
                        <FileTile
                          key={item.id}
                          item={item}
                          isLastElement={isLastElement}
                          lastElementRef={lastElementRef}
                          selected={selected}
                          isFresh={isFresh}
                          isAudio={type === 'AUDIO'}
                          isPlaying={!!mediaPreview?.audio?.isPlaying(item)}
                          onToggleSelect={(o) => onMediaChange({ checked: !selected, value: o.id })}
                          onPreview={openPreview}
                          t={t}
                        />
                      );
                    })}
                  </div>
                </>
              )}
              {isFetching && data?.length ? (
                <div style={{ textAlign: 'center', padding: '12px 0' }}>
                  <ProgressSpinner style={{ width: 28, height: 28 }} strokeWidth="3" />
                </div>
              ) : null}
            </>
          )}
        </ScrollContainer>

        {mediaPreview && <MiniPlayer />}

        <FirebaseMediaListener
          path={"/Companies/" + localStorage.getItem("company_id") + "/actions"}
          getData={() => dispatch(galleryRefetch())}
        />
      </div>
    );
  }

  return (
    <>
      <div className="w-full my-3 px-3">
        <div className="grid grid-cols-[1fr_auto] items-center gap-2">
          <div className="min-w-0"> {/* lets the input shrink */}
            <AddContentSearchInput isDisabled={data?.length === 0} />
          </div>
          <div className="shrink-0 hidden md:block">
            <UploadPopup currentFolder={currentFolder} content="media" />
          </div>
        </div>
      </div>
      <hr className="w-100 mt-0" />

      {breadCrumb.length !== 0 && (
        <div className="mt-4 mb-3 ps-1 d-flex justify-content-start w-full">
          <BreadCrumb
            breadCrumb={breadCrumb}
            setBreadCrumb={setBreadCrumb}
            setCurrentFolder={setCurrentFolder}
          />
        </div>
      )}

      <ScrollContainer path={`/media`} optionalParam={param}>
        <div className="d-flex flex-column scroll_container scroll_div px-3 w-100">
          <div className="d-flex flex-wrap py-3 w-100">
            {!isFetching && data?.length === 0 ? (
              <h5 className="py-5 text-center w-100">{t('addcontent.no_media_yet')}</h5>
            ) : (
              <>
                {data?.map((item, index) => {
                  const isLastElement = index === data?.length - 1;
                  return (
                    <div
                      key={`${item.id}`}
                      className={`${ids.indexOf(`${item.id}`) !== -1 ? "bg-gray-200" : ""} col-12 media_list ${index + 1 !== data?.length ? "border-bottom border-gray" : ""} mx-2 py-2 media position-relative`}
                    >
                      {!item.is_folder ? (
                        <div
                          className="col-12 d-flex align-items-center justify-content-between my-1"
                          key={`${item.type}-${index}`}
                          ref={isLastElement ? lastElementRef : null}
                        >
                          <label htmlFor={item.id} className="col-11 d-flex align-items-center">
                            <MediaCard item={item} displayAction={false} />
                          </label>
                          <EnhancedCheckbox
                            inputId={item.id}
                            name="media"
                            className="media_checkbox me-3"
                            value={item.id}
                            onChange={onMediaChange}
                            checked={ids.indexOf(`${item.id}`) !== -1}
                          />
                        </div>
                      ) : (
                        <div
                          className="col-12 d-flex align-items-center my-1"
                          key={`${item.type}-${index}`}
                          ref={isLastElement ? lastElementRef : null}
                        >
                          <FolderCard
                            setCurrentFolder={setCurrentFolder}
                            displayAction={false}
                            item={item}
                            setBreadCrumb={setBreadCrumb}
                            breadCrumb={breadCrumb}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
                {isFetching && data?.length ? (
                  <ProgressSpinner
                    className='h-[50px] w-[50px] flex justify-center items-center my-4'
                    strokeWidth="3"
                    fill="var(--surface-ground)"
                    animationDuration="30s"
                  />
                ) : null}
              </>
            )}
          </div>
        </div>
      </ScrollContainer>

      <FirebaseMediaListener
        path={"/Companies/" + localStorage.getItem("company_id") + "/actions"}
        getData={() => dispatch(galleryRefetch())}
      />

      {/* Floating Action Button for mobile upload */}
      <FloatingActionButton
        icon="fas fa-upload"
        label={t('addcontent.upload_media')}
        onClick={() => dispatch(toggleDialog("uploadMedia"))}
      />
    </>
  );
}

export default MediaList;
